import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ChevronRight, ChevronLeft, Zap, MonitorPlay, Timer, Star, CalendarDays, Gift } from 'lucide-react';
import { Link } from 'react-router-dom';
import itemService from '../../services/itemService';
import catalogService from '../../services/catalogService';
import ProductCard from '../common/ProductCard';
import { getImageUrl, generateSlug } from '../../utils/helpers';

// ─────────────────────────────────────────────────────────────
// COMPONENT MỚI: Skeleton cho Product Row (Khắc phục dứt điểm CLS)
// ─────────────────────────────────────────────────────────────
const ProductRowSkeleton = ({ isBestSeller, isFlashSale, isHotDeal }) => {
    let containerClass = "mb-6 p-3 md:p-4 rounded-xl shadow-sm border border-gray-100 bg-white";
    if (isFlashSale) {
        containerClass = "mb-6 p-3 md:p-4 rounded-xl shadow-sm bg-orange-50 border-none";
    } else if (isBestSeller) {
        containerClass = "mb-8 p-4 md:p-6 rounded-3xl shadow-xl bg-orange-100 border-none relative overflow-hidden";
    } else if (isHotDeal) {
        containerClass = "mb-6 p-3 md:p-4 rounded-xl shadow-sm border-2 border-rose-100 bg-rose-50";
    }

    return (
        <div className={containerClass}>
            <div className="flex flex-col md:flex-row md:items-start justify-between mb-5 pb-3 border-b border-black/5 gap-3 md:gap-4 relative z-10">
                <div className="w-48 md:w-64 h-7 bg-black/10 rounded animate-pulse"></div>
                <div className="w-24 md:w-32 h-7 bg-black/10 rounded-full animate-pulse hidden md:block"></div>
            </div>
            <div className="flex gap-3 overflow-hidden">
                {[...Array(5)].map((_, i) => (
                    <div key={i} className={`w-[calc(50%-6px)] sm:w-[calc(33.333%-8px)] md:w-[calc(25%-12px)] lg:w-[calc(20%-12.8px)] shrink-0 flex flex-col bg-white/50 border border-black/5 rounded-lg p-3 ${i > 1 ? 'hidden sm:flex' : ''} ${i > 2 ? 'hidden md:flex' : ''} ${i > 3 ? 'hidden lg:flex' : ''}`}>
                        <div className="w-full aspect-square bg-black/10 rounded-md animate-pulse mb-3"></div>
                        <div className="h-4 bg-black/10 rounded w-full animate-pulse mb-2"></div>
                        <div className="h-4 bg-black/10 rounded w-2/3 animate-pulse mb-4"></div>
                        <div className="h-6 bg-black/10 rounded w-1/2 mt-auto animate-pulse"></div>
                    </div>
                ))}
            </div>
        </div>
    );
};

const FlashSaleTimer = React.memo(({ endTime, onEnd }) => {
    const [timeLeft, setTimeLeft] = useState({ h: '00', m: '00', s: '00' });

    useEffect(() => {
        if (!endTime) return;
        const timer = setInterval(() => {
            const distance = endTime - Date.now();
            if (distance <= 0) {
                clearInterval(timer);
                setTimeLeft({ h: '00', m: '00', s: '00' });
                if (onEnd) onEnd();
            } else {
                const h = Math.floor(distance / 3600000);
                const m = Math.floor((distance % 3600000) / 60000);
                const s = Math.floor((distance % 60000) / 1000);
                setTimeLeft({
                    h: String(h).padStart(2, '0'),
                    m: String(m).padStart(2, '0'),
                    s: String(s).padStart(2, '0'),
                });
            }
        }, 1000);
        return () => clearInterval(timer);
    }, [endTime, onEnd]);

    return (
        <div className="flex items-center gap-1.5 bg-black/20 backdrop-blur-sm px-3 py-1 rounded-full border border-white/20 shrink-0">
            <Timer size={14} className="text-white animate-spin-slow" />
            <span className="text-xs font-bold text-white hidden md:inline mr-1">Kết thúc sau:</span>
            <div className="flex items-center gap-1 text-xs font-black text-white">
                {[timeLeft.h, timeLeft.m, timeLeft.s].map((t, i) => (
                    <React.Fragment key={i}>
                        <div className="bg-white text-tnc-orange w-6 h-6 flex items-center justify-center rounded shadow-inner">{t}</div>
                        {i < 2 && <span className="text-white/80 animate-pulse">:</span>}
                    </React.Fragment>
                ))}
            </div>
        </div>
    );
});

const LazyRowWrapper = ({ children, isLoading, isBestSeller, isFlashSale, isHotDeal }) => {
    const [isVisible, setIsVisible] = useState(false);
    const ref = useRef();

    useEffect(() => {
        const currentElement = ref.current;
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsVisible(true);
                    observer.disconnect();
                }
            },
            { rootMargin: '300px' }
        );
        
        if (currentElement) observer.observe(currentElement);
        
        return () => {
            if (currentElement) observer.unobserve(currentElement);
            observer.disconnect();
        };
    }, []);

    return (
        <div ref={ref}>
            {(isVisible && !isLoading) ? children : <ProductRowSkeleton isBestSeller={isBestSeller} isFlashSale={isFlashSale} isHotDeal={isHotDeal} />}
        </div>
    );
};

function getVisibleCount() {
    const w = window.innerWidth;
    if (w >= 1280) return 5;
    if (w >= 1024) return 4;
    if (w >= 640)  return 3;
    return 2;
}

export const ProductRow = React.memo(({
    title, items, link, isFlashSale, isBestSeller, isHotDeal, dateText,
    getImageUrl, subMenus, systemFlashSaleConfig, adminSpecs, hideViewAll
}) => {
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [visibleCount, setVisibleCount] = useState(getVisibleCount());
    const [isSaleActive, setIsSaleActive] = useState(isFlashSale);
    
    const isHoveredRef = useRef(false);

    useEffect(() => {
        const onResize = () => {
            setIsMobile(window.innerWidth < 768);
            setVisibleCount(getVisibleCount());
            setCurrentIndex(0);
        };
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);

    const totalItems  = items?.length || 0;
    const maxIndex    = Math.max(0, totalItems - visibleCount);
    const GAP_PX      = 16; 
    const slidePercent = 100 / visibleCount;

    const goTo = useCallback((index) => {
        if (isMobile) return;
        setCurrentIndex(prev => Math.max(0, Math.min(index, maxIndex)));
    }, [maxIndex, isMobile]);

    const goPrev = useCallback(() => {
        if (isMobile) return;
        setCurrentIndex(prev => Math.max(0, prev - 1));
    }, [isMobile]);

    const goNext = useCallback(() => {
        if (isMobile) return;
        setCurrentIndex(prev => (prev >= maxIndex ? 0 : prev + 1));
    }, [maxIndex, isMobile]);

    useEffect(() => {
        if (!items || totalItems <= visibleCount || isMobile) return;
        const timer = setInterval(() => {
            if (!isHoveredRef.current) goNext();
        }, 4500);
        return () => clearInterval(timer);
    }, [items, totalItems, visibleCount, goNext, isMobile]);

    if (!items || totalItems === 0 || (isFlashSale && !isSaleActive)) return null;

    let containerClass, titleClass, iconElem, borderLineClass, btnClass;
    if (isFlashSale) {
        containerClass  = "mb-6 p-3 md:p-4 rounded-xl shadow-sm bg-gradient-to-br from-tnc-orange-light via-tnc-orange to-tnc-orange-dark border-none shadow-orange-500/20";
        titleClass      = "text-white drop-shadow-md";
        iconElem        = <Zap size={24} className="text-yellow-300 animate-pulse fill-yellow-300" />;
        borderLineClass = "border-white/20";
        btnClass        = "bg-white text-tnc-orange border border-white/50 hover:bg-orange-50 hover:border-white hover:text-tnc-orange-hover";
    } else if (isBestSeller) {
        containerClass  = "mb-8 p-4 md:p-6 rounded-3xl shadow-xl bg-gradient-to-br from-orange-500 via-orange-400 to-orange-600 border border-orange-300 relative overflow-hidden";
        titleClass      = "text-white font-black drop-shadow-[0_2px_4px_rgba(194,65,12,0.8)]";
        iconElem        = <Star size={28} className="text-yellow-300 fill-yellow-300 animate-pulse drop-shadow-[0_0_8px_rgba(253,224,71,0.8)]" />;
        borderLineClass = "border-white/40";
        btnClass        = "bg-white text-orange-600 font-extrabold hover:bg-yellow-100 hover:text-orange-700 shadow-md border-none";
    } else if (isHotDeal) {
        containerClass  = "mb-6 p-3 md:p-4 rounded-xl shadow-sm border-2 border-rose-200/60 border-t-4 border-t-rose-500 bg-gradient-to-r from-rose-50 to-white";
        titleClass      = "text-rose-600";
        iconElem        = <Gift size={22} className="text-rose-500 fill-rose-500" />; 
        borderLineClass = "border-rose-200/60";
        btnClass        = "bg-white text-rose-500 hover:bg-rose-500 hover:text-white border border-rose-200 hover:border-rose-500";
    } else {
        containerClass  = "mb-6 p-3 md:p-4 rounded-xl shadow-sm border border-orange-300/60 border-t-4 border-t-tnc-orange bg-gradient-to-r from-orange-100 to-white";
        titleClass      = "text-blue-900";
        iconElem        = <MonitorPlay size={22} className="text-tnc-orange" />;
        borderLineClass = "border-orange-200/60";
        btnClass        = "bg-white text-tnc-orange hover:bg-tnc-orange hover:text-white border border-orange-200/60 hover:border-tnc-orange";
    }

    const translateValue = `calc(-${currentIndex * slidePercent}% - ${currentIndex * GAP_PX / visibleCount}px)`;
    const showPrev = currentIndex > 0;
    const showNext = totalItems > visibleCount;
    const totalDots = maxIndex + 1;
    const showDots  = totalDots > 1 && totalDots <= 8;

    return (
        <div className={containerClass}>
            {isBestSeller && (
                <>
                    <div className="absolute top-0 left-0 w-64 h-64 bg-white/30 blur-[80px] rounded-full pointer-events-none -translate-x-1/2 -translate-y-1/2" />
                    <div className="absolute bottom-0 right-0 w-64 h-64 bg-yellow-200/30 blur-[80px] rounded-full pointer-events-none translate-x-1/2 translate-y-1/2" />
                </>
            )}

            <div className={`flex flex-col md:flex-row md:items-start justify-between mb-5 pb-3 border-b gap-3 md:gap-4 relative z-10 ${borderLineClass}`}>
                <div className="flex items-center gap-3">
                    <h2 className={`text-base md:text-xl font-black uppercase flex items-center gap-2.5 tracking-wide whitespace-nowrap shrink-0 pt-0.5 ${titleClass}`}>
                        {iconElem}{title}
                    </h2>
                    {isHotDeal && dateText && (
                        <div className="hidden md:flex items-center gap-1.5 text-xs font-bold text-rose-600 bg-rose-100/80 px-3 py-1 rounded-full border border-rose-200 shrink-0">
                            <CalendarDays size={14} />
                            <span>{dateText}</span>
                        </div>
                    )}
                </div>

                <div className="flex-1 flex flex-wrap items-center justify-start md:justify-end gap-2 md:pr-2">
                    {isFlashSale && systemFlashSaleConfig?.end && (
                        <FlashSaleTimer endTime={systemFlashSaleConfig.end} onEnd={() => setIsSaleActive(false)} />
                    )}

                    {!isFlashSale && !isHotDeal && subMenus && subMenus.length > 0 && (
                        <div className="hidden md:flex flex-wrap items-center justify-end gap-2 lg:border-r lg:border-orange-200/60 lg:pr-3 w-full md:w-auto">
                            {subMenus.map((sub, idx) => (
                                <Link key={idx} to={sub.link} className="text-xs font-bold text-blue-800 bg-white hover:bg-orange-50 hover:text-tnc-orange px-3 py-1.5 rounded-full transition-colors whitespace-nowrap shrink-0 border border-orange-200/60 hover:border-tnc-orange shadow-sm">
                                    {sub.name}
                                </Link>
                            ))}
                        </div>
                    )}
                </div>

                {!hideViewAll && (
                    <Link to={link || '#'} className={`flex items-center justify-center gap-1 px-3 md:px-4 py-1.5 rounded-full text-xs font-bold transition-all active:scale-95 shadow-sm whitespace-nowrap shrink-0 ${btnClass}`}>
                        Xem tất cả <ChevronRight size={14} />
                    </Link>
                )}
            </div>

            <div className="relative z-10 px-0 md:px-10" 
                 onMouseEnter={() => isHoveredRef.current = true} 
                 onMouseLeave={() => isHoveredRef.current = false}>
                 
                {!isMobile && (
                    <button onClick={goPrev} disabled={!showPrev} aria-label="Trước"
                        className={`absolute left-0 md:-left-1 top-1/2 -translate-y-1/2 z-20 w-9 h-9 md:w-10 md:h-10 rounded-full items-center justify-center shadow-[0_4px_15px_rgba(0,0,0,0.15)] transition-all duration-200 hidden md:flex
                        ${isFlashSale  ? 'bg-white text-tnc-orange border border-orange-100' :
                          isBestSeller ? 'bg-white text-orange-600 hover:bg-yellow-100 border-none shadow-md' :
                          isHotDeal    ? 'bg-white text-rose-500 border border-rose-200 hover:bg-rose-50' :
                                         'bg-white border border-gray-200 text-gray-600 hover:text-tnc-orange'}
                        ${showPrev ? 'opacity-100 hover:scale-110' : 'opacity-0 pointer-events-none'}`}
                    >
                        <ChevronLeft size={22} />
                    </button>
                )}

                {isMobile ? (
                    <div className="flex gap-3 overflow-x-auto snap-x snap-mandatory scroll-smooth py-3 -my-3 w-full" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
                        {items.map(product => (
                            <div key={product.itemId} className="w-[calc(50%-6px)] sm:w-[calc(33.333%-8px)] snap-start shrink-0 flex">
                                <ProductCard product={product} getImageUrl={getImageUrl} adminSpecs={adminSpecs} />
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="overflow-hidden py-3 -my-3 w-full">
                        <div className="flex"
                            style={{
                                gap: `${GAP_PX}px`,
                                transform: `translateX(${translateValue})`,
                                transition: 'transform 0.55s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                                willChange: 'transform',
                            }}
                        >
                            {items.map(product => (
                                <div key={product.itemId} className={`w-[calc(33.333%-10.66px)] md:w-[calc(25%-12px)] lg:w-[calc(20%-12.8px)] shrink-0 flex ${isBestSeller ? 'transition-transform hover:-translate-y-1' : '' }`}>
                                    <ProductCard product={product} getImageUrl={getImageUrl} adminSpecs={adminSpecs} />
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {!isMobile && (
                    <button onClick={goNext} disabled={!showNext} aria-label="Tiếp"
                        className={`absolute right-0 md:-right-1 top-1/2 -translate-y-1/2 z-20 w-9 h-9 md:w-10 md:h-10 rounded-full items-center justify-center shadow-[0_4px_15px_rgba(0,0,0,0.15)] transition-all duration-200 hidden md:flex
                        ${isFlashSale  ? 'bg-white text-tnc-orange border border-orange-100' :
                          isBestSeller ? 'bg-white text-orange-600 hover:bg-yellow-100 border-none shadow-md' :
                          isHotDeal    ? 'bg-white text-rose-500 border border-rose-200 hover:bg-rose-50' :
                                         'bg-white border border-gray-200 text-gray-600 hover:text-tnc-orange'}
                        ${showNext ? 'opacity-100 hover:scale-110' : 'opacity-0 pointer-events-none'}`}
                    >
                        <ChevronRight size={22} />
                    </button>
                )}

                {!isMobile && showDots && (
                    <div className="flex justify-center gap-1.5 mt-4">
                        {Array.from({ length: totalDots }).map((_, i) => (
                            <button key={i} onClick={() => goTo(i)} aria-label={`Trang ${i + 1}`}
                                className={`rounded-full transition-all duration-300 ${
                                    i === currentIndex
                                        ? (isFlashSale || isBestSeller ? 'bg-white w-5 h-2' : isHotDeal ? 'bg-rose-500 w-5 h-2' : 'bg-tnc-orange w-5 h-2')
                                        : (isFlashSale || isBestSeller ? 'bg-white/40 w-2 h-2' : isHotDeal ? 'bg-rose-200 w-2 h-2' : 'bg-gray-300 w-2 h-2')
                                }`}
                            />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
});

const HomeProductSection = () => {
    const [loadingBestSellers, setLoadingBestSellers] = useState(true);
    const [bestSellers, setBestSellers] = useState([]);
    const [adminSpecs, setAdminSpecs] = useState([]);
    const [loadingDeals, setLoadingDeals] = useState(true);
    const [hotDeals, setHotDeals] = useState([]); 
    
    const [hotDealLimit, setHotDealLimit] = useState(10);
    // 👉 THÊM STATE LƯU LIMIT CHO BEST SELLER
    const [bestSellerLimit, setBestSellerLimit] = useState(10);

    const [flashSaleItems, setFlashSaleItems] = useState([]); 
    const [flashSaleConfig, setFlashSaleConfig] = useState({ start: null, end: null });
    const [loadingCatalog, setLoadingCatalog] = useState(true);
    const [groupedData, setGroupedData] = useState([]);

    useEffect(() => {
        let isMounted = true;
        const fetchEssential = async () => {
            try {
                const [specsRes, configRes, bestRes, dealsRes] = await Promise.all([
                    catalogService.getSpecs().catch(() => []),
                    catalogService.getSystemConfig().catch(() => ({})),
                    itemService.getBestSellers().catch(() => []),
                    itemService.getHotDeals().catch(() => ({ data: [] }))
                ]);
                
                if (!isMounted) return;

                const conf = configRes?.data || configRes || {};
                setFlashSaleConfig({
                    start: conf.flash_sale_start ? new Date(conf.flash_sale_start).getTime() : null,
                    end:   conf.flash_sale_end   ? new Date(conf.flash_sale_end).getTime()   : null
                });
                
                setHotDealLimit(parseInt(conf.home_hot_deal_limit) || 10);
                // 👉 LẤY LIMIT CHO BEST SELLER TỪ CONFIG HOẶC MẶC ĐỊNH LÀ 10
                setBestSellerLimit(parseInt(conf.product_detail_bestseller_limit) || 10);
                
                setAdminSpecs(Array.isArray(specsRes) ? specsRes : specsRes.data || []);
                setBestSellers(Array.isArray(bestRes) ? bestRes : bestRes.data || []);
                setHotDeals(dealsRes?.data || dealsRes || []);
            } catch(e) { 
                console.error(e); 
            } finally { 
                if (isMounted) {
                    setLoadingBestSellers(false);
                    setLoadingDeals(false); 
                }
            }
        };
        fetchEssential();

        return () => { isMounted = false; };
    }, []);

    useEffect(() => {
        let isMounted = true;
        const fetchProductData = async () => {
            try {
                const [allItemsRes, menuGroupsRes] = await Promise.all([
                    itemService.getHomepageItems().catch(() => ({ data: [] })),
                    itemService.getHomepageMenuGroups().catch(() => ({ data: [] }))
                ]);
                
                if (!isMounted) return;

                const allItems = allItemsRes?.data || allItemsRes || [];
                setFlashSaleItems(allItems.filter(p => p.itemFlashSalePrice > 0)); 
                
                const menuGroups = menuGroupsRes?.data || menuGroupsRes || [];
                setGroupedData(menuGroups);

            } catch(e) { 
                console.error(e); 
            } finally { 
                if (isMounted) {
                    setLoadingCatalog(false);
                }
            }
        };
        fetchProductData();

        return () => { isMounted = false; };
    }, []);

    const formatSaleDate = (dateString) => {
        if (!dateString) return "";
        try {
            return new Date(dateString).toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit', year: 'numeric' });
        } catch(e) { return ""; }
    };

    return (
        <section className="py-6 bg-gray-50 overflow-hidden">
            <div className="container mx-auto px-4 xl:px-8">
                
                {/* --- KHỐI 1: FLASH SALE & HOT DEALS --- */}
                {loadingDeals ? (
                    <>
                        <ProductRowSkeleton isFlashSale={true} />
                        <ProductRowSkeleton isHotDeal={true} />
                    </>
                ) : (
                    <>
                        {flashSaleItems.length > 0 && (
                            <LazyRowWrapper isFlashSale={true} isLoading={loadingDeals || loadingCatalog}>
                                <ProductRow
                                    title="GIỜ VÀNG DEAL SỐC"
                                    items={flashSaleItems}
                                    isFlashSale
                                    getImageUrl={getImageUrl}
                                    link="/flash-sale"
                                    systemFlashSaleConfig={flashSaleConfig}
                                    adminSpecs={adminSpecs}
                                />
                            </LazyRowWrapper>
                        )}
                        {hotDeals.map(sale => {
                            const dateText = (sale.saleStartDate && sale.saleEndDate) 
                                ? `${formatSaleDate(sale.saleStartDate)} - ${formatSaleDate(sale.saleEndDate)}` : null;
                            
                            const limitedItems = sale.items ? sale.items.slice(0, hotDealLimit) : [];
                            
                            if (limitedItems.length === 0) return null;

                            return (
                                <LazyRowWrapper key={`sale-${sale.saleId}`} isHotDeal={true}>
                                    <ProductRow
                                        title={sale.saleName || 'ƯU ĐÃI ĐẶC BIỆT'}
                                        items={limitedItems} 
                                        link={`/hot-deal`}
                                        getImageUrl={getImageUrl}
                                        adminSpecs={adminSpecs}
                                        isHotDeal={true}
                                        dateText={dateText}
                                    />
                                </LazyRowWrapper>
                            );
                        })}
                    </>
                )}

                {/* --- KHỐI 2: SẢN PHẨM BÁN CHẠY --- */}
                {loadingBestSellers ? (
                     <ProductRowSkeleton isBestSeller={true} />
                ) : (
                    bestSellers.length > 0 && (
                        <LazyRowWrapper isBestSeller={true}>
                            <ProductRow
                                title="SẢN PHẨM BÁN CHẠY"
                                // 👉 ÁP DỤNG GIỚI HẠN DỮ LIỆU ĐÃ LẤY TỪ ADMIN CHO BEST SELLER
                                items={bestSellers.slice(0, bestSellerLimit)}
                                isBestSeller
                                getImageUrl={getImageUrl}
                                adminSpecs={adminSpecs}
                                hideViewAll={true}
                            />
                        </LazyRowWrapper>
                    )
                )}

                {/* --- KHỐI 3: DANH MỤC SẢN PHẨM --- */}
                {loadingCatalog ? (
                    <>
                        <ProductRowSkeleton />
                        <ProductRowSkeleton />
                        <ProductRowSkeleton />
                    </>
                ) : (
                    groupedData.map(group => {
                        if (!group) return null;

                        let mainLink = `/danh-muc/${generateSlug(group.title || 'danh-muc')}-id${group.menuId}`;
                        if (group.menuUrl && group.menuUrl.includes('?')) {
                            mainLink += group.menuUrl.substring(group.menuUrl.indexOf('?'));
                        }

                        const subMenusList = (group.subMenus || []).map(sub => {
                            let subLink = `/danh-muc/${generateSlug(sub.menuName || 'danh-muc')}-id${sub.menuId}`;
                            if (sub.menuUrl && sub.menuUrl.includes('?')) {
                                subLink += sub.menuUrl.substring(sub.menuUrl.indexOf('?'));
                            }
                            return { name: sub.menuName, link: subLink };
                        });

                        return group.items && group.items.length > 0 && (
                            <LazyRowWrapper key={group.menuId} isLoading={loadingCatalog}>
                                <ProductRow
                                    title={group.title}
                                    link={mainLink}
                                    items={group.items}
                                    getImageUrl={getImageUrl}
                                    subMenus={subMenusList}
                                    adminSpecs={adminSpecs}
                                />
                            </LazyRowWrapper>
                        );
                    })
                )}
            </div>
        </section>
    );
};

export default HomeProductSection;